// Funnel/step-completion analysis for API usage analytics.
//
// Each unique (non-null) contactId is treated as one user session. For each
// step in the Predict Plans flow we compute how many sessions reached that
// step and the percentage of all sessions that did.
//
// Steps (matched against the hit's endpoint + method):
//   - Basic Plans:       GET  /contact/{id}/plans
//   - Health Conditions: PUT  /contact/{id}/healthConditions
//   - Medications:       PUT  /contact/{id}/medications
//   - Providers:         PUT  /contact/{id}/providers
//   - Predict Plans:     endpoint /ENGINE-NEW or /ENGINE-REPEAT for the contact

// Ordered step definitions. `match(hit)` returns true when a hit represents
// that step for its contact.
export const FUNNEL_STEPS = [
  {
    key: 'basicPlans',
    label: 'Retrieve Basic Plans',
    match: (hit) => hit.method === 'GET' && endsWith(hit.endpoint, '/plans')
  },
  {
    key: 'healthConditions',
    label: 'Set Health Conditions',
    match: (hit) => hit.method === 'PUT' && endsWith(hit.endpoint, '/healthConditions')
  },
  {
    key: 'medications',
    label: 'Set Medications',
    match: (hit) => hit.method === 'PUT' && endsWith(hit.endpoint, '/medications')
  },
  {
    key: 'providers',
    label: 'Set Providers',
    match: (hit) => hit.method === 'PUT' && endsWith(hit.endpoint, '/providers')
  },
  {
    key: 'predictPlans',
    label: 'Get Predict Plans',
    match: (hit) => hit.endpoint === '/ENGINE-NEW' || hit.endpoint === '/ENGINE-REPEAT'
  }
]

const endsWith = (endpoint, suffix) =>
  typeof endpoint === 'string' && endpoint.endsWith(suffix)

// Compute per-step completion across all unique contact sessions.
//
// @param {Array} hits - raw analytics rows (each with contactId, endpoint, method)
// @returns {{
//   totalSessions: number,
//   steps: Array<{ key, label, count, percent }>
// }}
export const computeFunnel = (hits) => {
  const rows = Array.isArray(hits) ? hits : []

  // Unique sessions = distinct non-null contactIds.
  const sessions = new Set()
  // Per-step sets of contactIds that reached the step.
  const reached = FUNNEL_STEPS.reduce((acc, step) => {
    acc[step.key] = new Set()
    return acc
  }, {})

  for (const hit of rows) {
    const contactId = hit && hit.contactId
    if (!contactId) continue
    sessions.add(contactId)
    for (const step of FUNNEL_STEPS) {
      if (step.match(hit)) reached[step.key].add(contactId)
    }
  }

  const totalSessions = sessions.size
  const steps = FUNNEL_STEPS.map((step) => {
    const count = reached[step.key].size
    const percent = totalSessions > 0 ? (count / totalSessions) * 100 : 0
    return { key: step.key, label: step.label, count, percent }
  })

  return { totalSessions, steps }
}

// Compute the funnel per customer (grouped by customerKeyId). Returns an array
// of { customerKeyId, ...computeFunnel(...) } sorted by customerKeyId, plus an
// overall funnel across all customers.
//
// @param {Array} hits - raw analytics rows
// @returns {{ overall: object, byCustomer: Array<{ customerKeyId, totalSessions, steps }> }}
export const computeFunnelByCustomer = (hits) => {
  const rows = Array.isArray(hits) ? hits : []

  const groups = new Map()
  for (const hit of rows) {
    const key = (hit && hit.customerKeyId) || 'unknown'
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(hit)
  }

  const byCustomer = Array.from(groups.entries())
    .map(([customerKeyId, customerHits]) => ({
      customerKeyId,
      ...computeFunnel(customerHits)
    }))
    .sort((a, b) => a.customerKeyId.localeCompare(b.customerKeyId))

  return { overall: computeFunnel(rows), byCustomer }
}

// Produce a chart-ready funnel series across all hits: starts with the total
// unique contact count, then the count reaching each funnel step in order.
// Each point is { label, count } suitable for a line/area chart.
//
// @param {Array} hits - raw analytics rows
// @returns {Array<{ label: string, count: number }>}
export const funnelChartData = (hits) => {
  const { totalSessions, steps } = computeFunnel(hits)
  return [
    { label: 'Contacts', count: totalSessions },
    ...steps.map((step) => ({ label: step.label, count: step.count }))
  ]
}

// Format a percentage to one decimal place with a trailing % sign.
export const formatPercent = (percent) => `${(Math.round(percent * 10) / 10).toFixed(1)}%`
